/**
 * Secret Reveal Dialog
 *
 * Shown after create or rotate-secret operations.
 * Per Design 5.1: Warning banner, monospace secret with copy, "I have saved it" button.
 * The cleartext secret is only available once — Facade masks it on subsequent reads.
 */

import { useState } from 'react';
import { AlertTriangle, Copy, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Button,
} from '@/components/ui';

interface SecretRevealDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  secret: string;
  onConfirm: () => void;
}

export function SecretRevealDialog({
  open,
  onOpenChange,
  secret,
  onConfirm,
}: SecretRevealDialogProps) {
  const { t } = useTranslation('webhook-config');
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(secret);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleConfirm = () => {
    setCopied(false);
    onConfirm();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>{t('secret.reveal.title')}</DialogTitle>
          <DialogDescription className="sr-only">
            {t('secret.reveal.warning')}
          </DialogDescription>
        </DialogHeader>

        {/* Warning banner */}
        <div className="flex items-start gap-3 rounded-md border border-yellow-500/30 bg-yellow-500/10 p-3">
          <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-warning" />
          <p className="text-sm font-medium text-warning-foreground dark:text-warning-foreground">
            {t('secret.reveal.warning')}
          </p>
        </div>

        {/* Secret display */}
        <div className="flex items-center gap-2 rounded-md border bg-muted/50 p-3">
          <code className="flex-1 break-all font-mono text-sm">{secret}</code>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 flex-shrink-0"
            onClick={handleCopy}
            title="Copy secret"
          >
            {copied ? (
              <Check className="h-4 w-4 text-success" />
            ) : (
              <Copy className="h-4 w-4" />
            )}
          </Button>
        </div>

        <DialogFooter>
          <Button onClick={handleConfirm} className="w-full">
            {t('secret.reveal.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
